'use client';

import React, { createContext, useContext } from 'react';
import { Drawer } from 'vaul';

type Direction = 'left' | 'right' | 'top' | 'bottom';

interface SheetContextValue {
  direction: Direction;
}

const SheetContext = createContext<SheetContextValue>({ direction: 'right' });

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  direction?: Direction;
}

interface SheetContentProps {
  children: React.ReactNode;
  className?: string;
  side?: Direction; // Optional override, defaults to context direction
}

interface SheetHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface SheetTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface SheetDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface SheetFooterProps {
  children: React.ReactNode;
  className?: string;
}

const Sheet = ({ open, onOpenChange, children, direction = 'right' }: SheetProps) => {
  return (
    <SheetContext.Provider value={{ direction }}>
      <Drawer.Root open={open} onOpenChange={onOpenChange} direction={direction}>
        {children}
      </Drawer.Root>
    </SheetContext.Provider>
  );
};

interface SheetTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const SheetTrigger = React.forwardRef<HTMLButtonElement, SheetTriggerProps>(
  (props, ref) => <Drawer.Trigger ref={ref} {...props} />
);
SheetTrigger.displayName = 'SheetTrigger';

interface SheetCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const SheetClose = React.forwardRef<HTMLButtonElement, SheetCloseProps>(
  (props, ref) => <Drawer.Close ref={ref} {...props} />
);
SheetClose.displayName = 'SheetClose';

const SheetPortal = ({ children }: { children: React.ReactNode }) => (
  <Drawer.Portal>{children}</Drawer.Portal>
);

interface SheetOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const SheetOverlay = React.forwardRef<HTMLDivElement, SheetOverlayProps>(
  ({ className = '', ...props }, ref) => (
    <Drawer.Overlay
      ref={ref}
      className={`fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 ${className}`}
      {...props}
    />
  )
);
SheetOverlay.displayName = 'SheetOverlay';

const SheetContent = React.forwardRef<
  React.ElementRef<typeof Drawer.Content>,
  SheetContentProps
>(({ side, className = '', children }, ref) => {
  const { direction } = useContext(SheetContext);
  const effectiveSide = side ?? direction;

  const sideStyles = {
    right: 'inset-y-0 right-0 h-full w-full max-w-md border-l border-gray-200 dark:border-gray-700',
    left: 'inset-y-0 left-0 h-full w-full max-w-md border-r border-gray-200 dark:border-gray-700',
    top: 'inset-x-0 top-0 w-full border-b border-gray-200 dark:border-gray-700',
    bottom: 'inset-x-0 bottom-0 w-full border-t border-gray-200 dark:border-gray-700',
  };

  return (
    <SheetPortal>
      <SheetOverlay />
      <Drawer.Content
        ref={ref}
        className={`fixed z-50 flex flex-col bg-white dark:bg-gray-800 shadow-xl focus:outline-none ${sideStyles[effectiveSide]} ${className}`}
      >
        {children}
      </Drawer.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = 'SheetContent';

const SheetHeader = ({ children, className = '' }: SheetHeaderProps) => (
  <div className={`flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700 ${className}`}>
    {children}
  </div>
);

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof Drawer.Title>,
  SheetTitleProps
>(({ children, className = '' }, ref) => (
  <Drawer.Title
    ref={ref}
    className={`text-2xl font-semibold text-gray-900 dark:text-white ${className}`}
  >
    {children}
  </Drawer.Title>
));
SheetTitle.displayName = 'SheetTitle';

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof Drawer.Description>,
  SheetDescriptionProps
>(({ children, className = '' }, ref) => (
  <Drawer.Description
    ref={ref}
    className={`mt-1 text-sm text-gray-500 dark:text-gray-400 ${className}`}
  >
    {children}
  </Drawer.Description>
));
SheetDescription.displayName = 'SheetDescription';

const SheetBody = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`flex-1 overflow-y-auto p-6 ${className}`}>
    {children}
  </div>
);

const SheetFooter = ({ children, className = '' }: SheetFooterProps) => (
  <div className={`flex-shrink-0 p-6 border-t border-gray-200 dark:border-gray-700 ${className}`}>
    {children}
  </div>
);

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
};
